import { useEffect, useState } from 'react';
import {
  getDefaultPostOnboardingTutorialState,
  readPostOnboardingTutorialState,
  tutorialStepOrder,
  writePostOnboardingTutorialState,
  type StoredPostOnboardingTutorialState,
  type TutorialStepId,
} from './postOnboardingTutorialStorage';

type UsePostOnboardingTutorialStateArgs = {
  userId: string | null;
  organisationId: string | null;
  projectId: string | null;
  onboardingCompleted: boolean;
  onboardingJustCompleted: boolean;
  forceOpen?: boolean;
};

type UsePostOnboardingTutorialStateResult = {
  currentStepId: TutorialStepId;
  currentStepIndex: number;
  totalSteps: number;
  isOpen: boolean;
  canResume: boolean;
  openTutorial: () => void;
  dismissTutorial: () => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  completeTutorial: () => void;
};

const nowIso = (): string => new Date().toISOString();

const getStepIndex = (stepId: TutorialStepId): number => {
  const index = tutorialStepOrder.indexOf(stepId);
  return index >= 0 ? index : 0;
};

export const usePostOnboardingTutorialState = ({
  userId,
  organisationId,
  projectId,
  onboardingCompleted,
  onboardingJustCompleted,
  forceOpen = false,
}: UsePostOnboardingTutorialStateArgs): UsePostOnboardingTutorialStateResult => {
  const [storedState, setStoredState] = useState<StoredPostOnboardingTutorialState>(
    getDefaultPostOnboardingTutorialState(),
  );
  const [hasStoredState, setHasStoredState] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const canPersist =
    userId !== null && organisationId !== null && projectId !== null && onboardingCompleted;

  useEffect(() => {
    if (!canPersist) {
      setStoredState(getDefaultPostOnboardingTutorialState());
      setHasStoredState(false);
      setIsOpen(false);
      return;
    }

    const persisted = readPostOnboardingTutorialState(userId, organisationId, projectId);
    const resolvedState = persisted ?? getDefaultPostOnboardingTutorialState();
    const shouldAutoOpen = forceOpen;
    const nextState =
      shouldAutoOpen && resolvedState.seenAt === null
        ? { ...resolvedState, seenAt: nowIso() }
        : resolvedState;

    if (shouldAutoOpen) {
      writePostOnboardingTutorialState(userId, organisationId, projectId, nextState);
    }

    setStoredState(nextState);
    setHasStoredState(persisted !== null || shouldAutoOpen);
    setIsOpen(shouldAutoOpen);
  }, [canPersist, forceOpen, onboardingJustCompleted, organisationId, projectId, userId]);

  const persistState = (nextState: StoredPostOnboardingTutorialState): void => {
    if (!canPersist || !userId || !organisationId || !projectId) return;
    writePostOnboardingTutorialState(userId, organisationId, projectId, nextState);
    setStoredState(nextState);
    setHasStoredState(true);
  };

  const openTutorial = (): void => {
    const nextState =
      storedState.seenAt === null
        ? { ...storedState, seenAt: nowIso(), dismissedAt: null }
        : { ...storedState, dismissedAt: null };
    persistState(nextState);
    setIsOpen(true);
  };

  const dismissTutorial = (): void => {
    persistState({ ...storedState, dismissedAt: nowIso() });
    setIsOpen(false);
  };

  const goToNextStep = (): void => {
    const currentStepIndex = getStepIndex(storedState.currentStepId);
    const nextStepId =
      tutorialStepOrder[Math.min(currentStepIndex + 1, tutorialStepOrder.length - 1)];
    persistState({ ...storedState, currentStepId: nextStepId });
  };

  const goToPreviousStep = (): void => {
    const currentStepIndex = getStepIndex(storedState.currentStepId);
    const previousStepId = tutorialStepOrder[Math.max(currentStepIndex - 1, 0)];
    persistState({ ...storedState, currentStepId: previousStepId });
  };

  const completeTutorial = (): void => {
    persistState({
      ...storedState,
      dismissedAt: null,
      completedAt: nowIso(),
      currentStepId: tutorialStepOrder[tutorialStepOrder.length - 1],
    });
    setIsOpen(false);
  };

  return {
    currentStepId: storedState.currentStepId,
    currentStepIndex: getStepIndex(storedState.currentStepId),
    totalSteps: tutorialStepOrder.length,
    isOpen,
    canResume:
      canPersist &&
      hasStoredState &&
      storedState.seenAt !== null &&
      storedState.completedAt === null &&
      !isOpen,
    openTutorial,
    dismissTutorial,
    goToNextStep,
    goToPreviousStep,
    completeTutorial,
  };
};
